import { useEffect, useState, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

interface MartColumn {
  readonly name: string;
  readonly dataType: string;
  readonly isMeasure: boolean;
}

interface ExplorerResponse {
  readonly martCode: string;
  readonly martName: string;
  readonly columns: readonly MartColumn[];
  readonly rows: ReadonlyArray<Record<string, string | number>>;
}

/**
 * GR L26.02 — Mart Explorer (`/dwh/marts/{id}/explorer`). Sandboxed, read-only
 * exploration of a single published mart: the semantic-layer-enforced column
 * catalogue (dimensions vs measures) plus a bounded preview slice. Every
 * executed query is logged to `dwh_query_log.query_text` server-side; this
 * screen only issues the governed preview request for the mart in the route.
 */
export function GrMartExplorerPage(): ReactNode {
  const { t } = useTranslation();
  const { token } = useAuth();
  const { martId } = useParams<{ martId: string }>();
  const [data, setData] = useState<ExplorerResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    apiRequest<{ data: ExplorerResponse }>(`/v1/dwh/marts/${martId}`, { token })
      .then((r) => { if (alive) { setData(r.data); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, martId]);

  return (
    <main aria-label="mart-explorer">
      <h1>{t('gr26.explorer.title')}</h1>
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{t('gr26.explorer.loading')}</p> : data && (
        <>
          <p aria-label="mart-heading">{data.martCode} — {data.martName}</p>
          <p aria-label="column-count">{t('gr26.explorer.columnCount', { count: String(data.columns.length) })}</p>
          <table aria-label="column-table">
            <thead><tr><th>{t('gr26.explorer.column')}</th><th>{t('gr26.explorer.type')}</th><th>{t('gr26.explorer.role')}</th></tr></thead>
            <tbody>
              {data.columns.map((c) => (
                <tr key={c.name}>
                  <td>{c.name}</td><td>{c.dataType}</td>
                  <td>{c.isMeasure ? t('gr26.explorer.measure') : t('gr26.explorer.dimension')}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p aria-label="row-count">{t('gr26.explorer.rowCount', { count: String(data.rows.length) })}</p>
          <table aria-label="preview-table">
            <thead><tr>{data.columns.map((c) => <th key={c.name}>{c.name}</th>)}</tr></thead>
            <tbody>
              {data.rows.map((r, i) => (
                <tr key={i}>{data.columns.map((c) => <td key={c.name}>{String(r[c.name] ?? '')}</td>)}</tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </main>
  );
}
